/**
 * Order Scheduler — Session-level date, time & order-type management
 * ------------------------------------------------------------------
 * Persists the user's chosen pickup/delivery date, time-slot and order type
 * (pickup vs delivery) in sessionStorage so the selection survives page
 * navigations but resets when the browser tab closes.
 *
 * Other scripts (date-filter.js, product.js, app.js) read from this module
 * via the global `window.OrderScheduler` API.
 */
(function () {
  'use strict';

  var KEYS = {
    date: 'cc_order_date',          // e.g. "14 February 2026"
    dayName: 'cc_order_day',        // e.g. "Tuesday"
    time: 'cc_order_time',          // e.g. "10:00 AM - 11:00 AM"
    type: 'cc_order_type'           // "Pickup" | "Delivery"
  };

  var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  /* ── One-off CLOSED / HOLIDAY dates ──────────────────────────────────
     Dates the bakery is closed ON TOP OF the recurring Sun/Mon closure.
     Format: 'YYYY-MM-DD' (zero-padded), matched at local day granularity.
     Disabled in EVERY scheduler-driven picker (cake/cookie/jar PDPs, the
     pop-up picker, the cart inline picker) via getDisabledDateFn below, and
     also read by the Build-a-Box calendar through OrderScheduler.isClosedDate().
     ⚠️ To add a future closure: add the date here AND mirror it in the checkout
     Validation Function (cart_validations_generate_run.js → CLOSED_DAYS), which
     keeps its own copy for the bypass-proof server-side block. */
  var CLOSED_DATES = [
    '2026-07-04'   // Independence Day — store closed (a Saturday, normally open)
  ];

  /* ── DELIVERY-ONLY blackout dates ────────────────────────────────────
     Days the bakery is OPEN (pickup still allowed) but CANNOT deliver.
     Unlike CLOSED_DATES (a full closure that blocks BOTH pickup and delivery),
     a date here is disabled in the picker ONLY when the customer's order type
     is "Delivery" — pickup for the same day stays available. Format
     'YYYY-MM-DD' (zero-padded), matched at local day granularity.
     Bypass-proof server-side half lives in the checkout Validation Function
     (cart_validations_generate_run.js → DELIVERY_CLOSED_DAYS).
     ⚠️ To add a delivery blackout: add the date here AND mirror it there. */
  var DELIVERY_CLOSED_DATES = [
    '2026-07-11'   // Sat July 11, 2026 — no deliveries (pickup still OK)
  ];
  function ymdLocal(date) {
    return date.getFullYear() + '-' +
           String(date.getMonth() + 1).padStart(2, '0') + '-' +
           String(date.getDate()).padStart(2, '0');
  }

  /* ── helpers ─────────────────────────────────────────────────────── */
  function get(key) { return sessionStorage.getItem(key) || ''; }
  function set(key, val) { sessionStorage.setItem(key, val); }
  function remove(key) { sessionStorage.removeItem(key); }

  /* ── public API (attached to window.OrderScheduler) ──────────────── */
  var api = {
    /* Date ----------------------------------------------------------*/
    getDate: function () { return get(KEYS.date); },
    getDayName: function () { return get(KEYS.dayName); },
    setDate: function (dateStr, dateObj) {
      set(KEYS.date, dateStr);
      if (dateObj) {
        set(KEYS.dayName, DAY_NAMES[dateObj.getDay()]);
      }
      dispatchChange();
    },
    clearDate: function () {
      remove(KEYS.date);
      remove(KEYS.dayName);
      dispatchChange();
    },

    /* Time ----------------------------------------------------------*/
    getTime: function () { return get(KEYS.time); },
    setTime: function (timeStr) {
      set(KEYS.time, timeStr);
      dispatchChange();
    },
    clearTime: function () {
      remove(KEYS.time);
      dispatchChange();
    },

    /* Order type (Pickup / Delivery) --------------------------------*/
    getType: function () { return get(KEYS.type); },
    setType: function (typeStr) {
      set(KEYS.type, typeStr);
      dispatchChange();
    },
    clearType: function () {
      remove(KEYS.type);
      dispatchChange();
    },

    /* Composite helpers ---------------------------------------------*/
    isReady: function () {
      // Date + type + time are ALL required before add-to-cart is allowed.
      // Time was previously optional (or delivery-only); now both Pickup (10am–6pm)
      // and Delivery (11am–3pm) require an explicit slot. Drives:
      //   • popup Confirm button (snippets/order-scheduler-ui.liquid)
      //   • product-form submit gate (assets/order-scheduler-product.js)
      //   • the inline gate message under Add-to-Cart
      return !!(api.getDate() && api.getType() && api.getTime());
    },
    getSummary: function () {
      var parts = [];
      if (api.getType()) parts.push(api.getType());
      if (api.getDate()) parts.push(api.getDate());
      // Time is now collected for BOTH Pickup and Delivery (was previously delivery-only).
      if (api.getTime()) parts.push(api.getTime());
      return parts.join(' \u2022 ');
    },
    getLineProperties: function () {
      var props = {};
      if (api.getType()) props['Order Type'] = api.getType();
      if (api.getDate()) props['Pickup/Delivery Date'] = api.getDate();
      // Time captured for both Pickup and Delivery so it lands on the order line item.
      if (api.getTime()) props['Pickup/Delivery Time'] = api.getTime();
      return props;
    },
    clearAll: function () {
      remove(KEYS.date);
      remove(KEYS.dayName);
      remove(KEYS.time);
      remove(KEYS.type);
      dispatchChange();
    },

    /**
     * Apply day-of-week + order-type visibility rules to every time-slot
     * <select> in the DOM. Used by both the popup (snippets/order-scheduler-ui.liquid)
     * and the inline sidebar (snippets/date-filter-sidebar.liquid).
     *
     *   PICKUP, Tue-Fri  → 10AM–6PM (slots with data-start-hour 10..17)
     *   PICKUP, Saturday → 10AM–4PM (slots 10..15)
     *   DELIVERY, any    → 11AM–3PM (slots 11..14)
     *
     * If the currently-selected option gets filtered out, clear it so the
     * user must re-pick a valid slot.
     *
     * The selects are detected by ID substring — anything ending with
     * `time-select` is a time slot select. Selects whose ID contains
     * `pickup` apply pickup rules; everything else applies delivery rules.
     *
     * Mike + Renee + Chris, 2026-05-19 — expanded pickup hours so customers
     * can pick up after work.
     */
    applyTimeSlotVisibility: function () {
      var selects = document.querySelectorAll('select[id$="time-select"]');
      if (!selects.length) return;

      var type = api.getType();           // 'Pickup' | 'Delivery' | null
      var dayName = api.getDayName();      // 'Saturday', 'Tuesday' etc. | null
      var currentTime = api.getTime();

      var anyClearedSelection = false;

      selects.forEach(function (sel) {
        var isPickupSelect = /pickup/i.test(sel.id);

        // Determine the active window for THIS select.
        // The select might be hidden (the wrapper has display:none for the
        // other order type), but we still set visibility on its options so
        // that when the wrapper is shown, only valid slots appear.
        var minHour, maxHour;
        if (isPickupSelect) {
          // Pickup rules depend on the selected day.
          if (dayName === 'Saturday') {
            minHour = 10; maxHour = 15;  // 10AM-4PM (last slot 3-4)
          } else {
            // Default to Tue-Fri window. Even if no date is selected yet,
            // showing the full window is the right default — the customer
            // can see ALL pickup options up front and we narrow when they
            // pick a Saturday.
            minHour = 10; maxHour = 17;  // 10AM-6PM (last slot 5-6)
          }
        } else {
          minHour = 11; maxHour = 14;    // Delivery: 11AM-3PM (unchanged)
        }

        var selectedStillVisible = false;
        var opts = sel.querySelectorAll('option[data-start-hour]');
        opts.forEach(function (opt) {
          var sh = parseInt(opt.getAttribute('data-start-hour'), 10);
          var visible = !isNaN(sh) && sh >= minHour && sh <= maxHour;
          opt.hidden = !visible;
          opt.disabled = !visible;
          if (visible && opt.value === currentTime) selectedStillVisible = true;
        });

        // If the persisted time slot no longer matches a visible option,
        // clear it so the customer is forced to re-pick something valid.
        if (currentTime && sel.value === currentTime && !selectedStillVisible) {
          sel.value = '';
          anyClearedSelection = true;
        }
      });

      if (anyClearedSelection) {
        // Don't loop via dispatchChange — set directly and notify via change
        // event so UI handlers re-sync without re-running this function.
        remove(KEYS.time);
        dispatchChange();
      }
    },

    /* Day-tag validation for product pages --------------------------*/
    /**
     * Given a comma-separated string of day tags (from `data-day-tags`),
     * returns true if the product is available on the currently selected day.
     * Products with NO day tags are always available.
     */
    isProductAvailableOnSelectedDay: function (dayTagsStr) {
      if (!dayTagsStr || dayTagsStr.trim() === '') return true; // always-available
      var selectedDay = api.getDayName();
      if (!selectedDay) return true; // no day selected yet — allow
      var tags = dayTagsStr.split(',').map(function (d) { return d.trim(); });
      return tags.indexOf(selectedDay) !== -1;
    },

    /**
     * Returns the day names a product IS available on (from its tags).
     * If empty array, product is always available.
     */
    getProductAvailableDays: function (dayTagsStr) {
      if (!dayTagsStr || dayTagsStr.trim() === '') return [];
      return dayTagsStr.split(',').map(function (d) { return d.trim(); });
    },

    /**
     * Date restriction logic for pickup/delivery:
     * - Default minimum lead time: 24h (orders placed today are earliest tomorrow).
     * - Cake product pages set `data-min-lead-hours="48"` on the scheduler container,
     *   which pushes the earliest selectable date 48 hours out (i.e., today+2 days).
     *   The cake-page hint comes from `snippets/order-scheduler-ui.liquid` which checks
     *   `template contains 'product.cake'` and emits the data attribute on the popup root.
     * - On top of that, if the customer is placing the order after 10pm EST, the cutoff
     *   adds another day (orders placed Friday at 10:01pm EST cannot be delivered/picked up
     *   until Sunday at the earliest — and Sunday is then disabled by `getDisabledDateFn`).
     *   (Updated 2026-05-14 per Renee: was 6pm, bumped to 10pm to give late-night
     *   customers another chance to place a next-day order.)
     * Returns the minimum allowed Date object (with time zeroed out).
     */
    getMinDate: function () {
      // Read the lead-time hint from any element on the page (popup or inline picker).
      // Falls back to 24h if no hint is present (preserves prior behavior on unrelated pages).
      var leadEl = document.querySelector('[data-min-lead-hours]');
      var minHours = leadEl ? parseInt(leadEl.getAttribute('data-min-lead-hours'), 10) : 24;
      if (isNaN(minHours) || minHours < 24) minHours = 24;
      var daysToAdd = Math.ceil(minHours / 24);

      // Get current time in EST (Eastern Standard Time / Eastern Time)
      var now = new Date();
      var estOffset = api._getESTOffset(now);
      var estNow = new Date(now.getTime() + estOffset);

      var estHour = estNow.getUTCHours();

      // Start by adding the lead-time days (24h → +1, 48h → +2, etc.)
      var minDate = new Date(now);
      minDate.setDate(minDate.getDate() + daysToAdd);

      // Cutoff: if it's 10pm EST or later, push another day on top of the lead-time
      // floor. (Was 6pm before 2026-05-14 — bumped per Renee so late-night customers
      // can still order for tomorrow.)
      if (estHour >= 22) {
        minDate.setDate(minDate.getDate() + 1);
      }

      // Zero out time for clean comparison
      minDate.setHours(0, 0, 0, 0);
      return minDate;
    },

    /**
     * Get offset in ms to convert local time to EST/EDT UTC time.
     * Accounts for daylight saving time.
     */
    _getESTOffset: function (date) {
      // Create a date string in the America/New_York timezone
      var formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
      });
      var parts = formatter.formatToParts(date);
      var estParts = {};
      parts.forEach(function (p) { estParts[p.type] = p.value; });
      var estDate = new Date(Date.UTC(
        parseInt(estParts.year),
        parseInt(estParts.month) - 1,
        parseInt(estParts.day),
        parseInt(estParts.hour === '24' ? '0' : estParts.hour),
        parseInt(estParts.minute),
        parseInt(estParts.second)
      ));
      return estDate.getTime() - date.getTime();
    },

    /**
     * The earliest date the customer can ACTUALLY pick — i.e., the first date
     * that's >= getMinDate() AND isn't disabled (Sun/Mon are closed).
     * Used by the popup + inline pickers to pre-fill a sensible default so the
     * customer doesn't see "today" (which they can't actually order for).
     * Walks forward up to 14 days as a safety bound.
     */
    getFirstAvailableDate: function () {
      var disableFn = api.getDisabledDateFn();
      var minDate = api.getMinDate();
      var d = new Date(minDate);
      d.setHours(0, 0, 0, 0);
      for (var i = 0; i < 14; i++) {
        if (!disableFn(d)) return d;
        d.setDate(d.getDate() + 1);
      }
      return minDate;
    },

    /**
     * Maximum advance-booking window. Customers can't pick a date more than
     * this many days from today. Originally added 2026-05-08 to prevent
     * orders dated weeks/months out — confusing for the bakery and gives
     * Renee/Chris no way to plan inventory that far ahead.
     *
     * Applies to ALL products (cupcakes, cakes, cookies, jar cakes, custom
     * boxes, picks). To change the window, edit MAX_ADVANCE_DAYS below.
     */
    getMaxDate: function () {
      var MAX_ADVANCE_DAYS = 21;
      var d = new Date();
      d.setDate(d.getDate() + MAX_ADVANCE_DAYS);
      d.setHours(23, 59, 59, 999);
      return d;
    },

    /* True if `date` is a one-off bakery closure / holiday (see CLOSED_DATES).
       Exposed so the Build-a-Box calendar (order-wizard.js) shares this single
       source of truth instead of keeping its own holiday list. */
    isClosedDate: function (date) {
      if (!date) return false;
      return CLOSED_DATES.indexOf(ymdLocal(date)) !== -1;
    },

    /* True if `date` is a DELIVERY-ONLY blackout day (see DELIVERY_CLOSED_DATES) —
       the bakery is open for pickup but can't deliver. Exposed so every picker
       (popup, inline, Build-a-Box) can share one source of truth. */
    isDeliveryClosedDate: function (date) {
      if (!date) return false;
      return DELIVERY_CLOSED_DATES.indexOf(ymdLocal(date)) !== -1;
    },

    /**
     * Flatpickr disable function: disables Sunday, Monday, one-off holiday
     * closures (CLOSED_DATES), past dates, dates that violate the lead-time
     * cutoff, and dates beyond the MAX_ADVANCE_DAYS booking window.
     *
     * Order-type aware: when the customer's current order type is "Delivery"
     * (or an explicit type is passed in), DELIVERY_CLOSED_DATES are also
     * disabled — pickup for those same days stays selectable. The type is read
     * LIVE from the passed arg or getType() each time the returned fn runs, so
     * re-applying the disable (fp.set('disable', ...)) after a type change picks
     * up the new rule without rebuilding it.
     *
     * @param {string} [orderType] optional "Pickup"|"Delivery"; falls back to getType()
     */
    getDisabledDateFn: function (orderType) {
      var minAllowed = api.getMinDate();
      var maxAllowed = api.getMaxDate();
      return function (date) {
        var day = date.getDay();
        // Disable Sunday (0) and Monday (1)
        if (day === 0 || day === 1) return true;
        // Disable one-off full closures / holidays (e.g., July 4) — both types
        if (api.isClosedDate(date)) return true;
        // Disable delivery-only blackout days when the order is a Delivery
        var type = orderType || api.getType();
        if (type === 'Delivery' && api.isDeliveryClosedDate(date)) return true;
        // Disable dates before min allowed
        var compare = new Date(date);
        compare.setHours(0, 0, 0, 0);
        if (compare < minAllowed) return true;
        // Disable dates beyond the 21-day advance window
        if (compare > maxAllowed) return true;
        return false;
      };
    }
  };

  /* ── Custom event so other code can react ────────────────────────── */
  function dispatchChange() {
    document.dispatchEvent(new CustomEvent('order-scheduler:change', {
      detail: {
        date: api.getDate(),
        dayName: api.getDayName(),
        time: api.getTime(),
        type: api.getType(),
        ready: api.isReady()
      }
    }));
  }

  window.OrderScheduler = api;
})();
